import { Activity } from 'lucide-react'
import type { ActivityEvent } from '../../services/historyTypes'
import { activityIcon, activityVerb, activityClockFull, groupActivityByMinute } from './activityFormat'

// Shared presentational activity feed. Renders an event list as a
// per-minute-grouped vertical timeline: each minute gets a sticky time
// header, and events flow down a rail of colour-coded icon nodes with
// the actor name bold and the action muted. Used by the live side-panel
// tab (ActivityLogPanel) and the gated session-history detail.
export default function ActivityFeed({
  events,
  stickyHeaders = false,
}: {
  events: ActivityEvent[]
  /** Stick the minute headers to the top while scrolling. On for the
   *  live panel (its own scroll container); off in history (the page
   *  scrolls, so a sticky header would detach from its section). */
  stickyHeaders?: boolean
}) {
  if (events.length === 0) {
    return (
      <div className="flex h-full min-h-[120px] flex-col items-center justify-center gap-2 text-muted-foreground">
        <Activity size={20} className="opacity-40" />
        <span className="text-sm">No activity yet</span>
      </div>
    )
  }

  const groups = groupActivityByMinute(events)

  return (
    <div className="pb-2">
      {groups.map((group) => (
        <section key={group.key} aria-label={group.label}>
          <div
            className={`flex items-center gap-2 px-3 py-1 ${
              stickyHeaders ? 'sticky top-0 z-10 bg-card/95 backdrop-blur-sm' : ''
            }`}
          >
            <time className="text-[10px] font-semibold uppercase tracking-wider tabular-nums text-muted-foreground">
              {group.label}
            </time>
            <span className="h-px flex-1 bg-border/70" />
          </div>

          <ul className="px-3">
            {group.items.map((e, i) => {
              const { Icon, cls } = activityIcon(e.type)
              const isLast = i === group.items.length - 1
              return (
                <li
                  key={`${e.ts}-${e.actorId}-${e.type}-${i}`}
                  className="group flex items-stretch gap-2.5"
                >
                  {/* Timeline rail: colour-coded node + connecting line. */}
                  <div className="flex flex-col items-center">
                    <span className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full bg-muted ring-1 ring-border">
                      <Icon size={12} className={cls} />
                    </span>
                    {!isLast && <span className="my-0.5 w-px flex-1 bg-border/60" />}
                  </div>

                  <div className="flex min-w-0 flex-1 items-start gap-2 pb-3 pt-[3px]">
                    <p className="min-w-0 text-sm leading-snug">
                      <span className="font-medium text-foreground">{e.actorName}</span>{' '}
                      <span className="text-muted-foreground">{activityVerb(e)}</span>
                    </p>
                    <time
                      dateTime={new Date(e.ts).toISOString()}
                      title={activityClockFull(e.ts)}
                      className="ml-auto shrink-0 pt-px text-[10px] tabular-nums text-muted-foreground/50 transition-colors group-hover:text-muted-foreground"
                    >
                      {activityClockFull(e.ts)}
                    </time>
                  </div>
                </li>
              )
            })}
          </ul>
        </section>
      ))}
    </div>
  )
}
